import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  COMMON_LIST_OPTIONS,
  CommonListColumn,
  CommonListComponent,
  CommonListOptions,
} from '@flexyoo/shared/ui';
import { EmployeeBudgetListDataSource } from './employee-budget-list.data-source';
import { EmployeeBudgetSummary } from '@flexyoo/employee/models';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeBudgetListFilterComponent } from './employee-budget-list-filter/employee-budget-list-filter.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { EmployeeBudgetDataAccessService } from '@flexyoo/employee/data-access';
import { LangService } from '@flexyoo/shared/services';
import { getEmployeeFundingTypeLabelKey } from '@flexyoo/shared/functions';

@Component({
  selector: 'flexyoo-employee-budget-list',
  templateUrl: 'employee-budget-list.component.html',
  styleUrls: ['./employee-budget-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonListComponent, EmployeeBudgetListFilterComponent],
  providers: [
    {
      provide: COMMON_LIST_OPTIONS,
      useValue: {
        mainColumn: false,
        columnTitlePrefix: 'funding',
        rowClass: (row: EmployeeBudgetSummary) =>
          row.type === 'Summary' ? 'summary-row' : undefined,
      } as CommonListOptions,
    },
  ],
})
export class FlexyooEmployeeBudgetListComponent {
  dataSource = new EmployeeBudgetListDataSource();
  private readonly langService = inject(LangService);
  private readonly translate = inject(TranslateService);
  years = toSignal(inject(EmployeeBudgetDataAccessService).getEmployeeFundingYears());
  readonly columns: CommonListColumn[] = [
    {
      id: 'assignmentDate',
      title: 'funding.assignment-date',
      type: {
        kind: 'date',
      },
      isSortable: true,
    },
    {
      id: 'name',
      type: {
        kind: 'value',
        value: (row: any) => {
          let name = '';
          switch (this.langService.lang) {
            case 'nl':
              name = row.nameNl;
              break;
            case 'fr':
              name = row.nameFr;
              break;
            case 'en':
              name = row.nameEn;
              break;
          }
          return name || row.nameEn || row.nameFr || row.nameNl;
        },
      },
      isSortable: true,
    },
    {
      id: 'type',
      type: {
        kind: 'value',
        value: (row: EmployeeBudgetSummary) =>
          this.translate.instant(
            row.type === 'Summary' ? 'funding.summary' : getEmployeeFundingTypeLabelKey(row.type)
          ),
      },
      isSortable: true,
    },
    {
      id: 'grossAmount',
      title: 'funding.gross-amount',
      type: {
        kind: 'number',
        format: 'currency',
        value: (row: any) => row.grossAmount,
      },
      isSortable: true,
    },
    {
      id: 'nsso',
      title: 'funding.onss',
      type: {
        kind: 'number',
        format: 'percent',
      },
      isSortable: true,
    },
    {
      id: 'annualHolidayFee',
      title: 'funding.annual-holiday-fee',
      type: {
        kind: 'number',
        format: 'percent',
      },
      isSortable: true,
    },
    {
      id: 'budgetAmount',
      title: 'funding.budget-amount',
      type: {
        kind: 'number',
        format: 'currency',
        value: (row: any) => row.budgetAmount,
      },
      isSortable: true,
    },
  ];
}
